import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { adminMainUrl } from "../../../../Config";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
  getBranchCity,
  getBranchCountry,
  getBranchState,
} from "../../../../Redux/action/Organisation/Branch/BranchAction";

const LedgerAccountContactInfo = ({
  ledger_account_id,
  contact,
  onContactChange,
  addContact,
  removeContact,
  saveAndNext,
  tabId,
  backId,
  clearGroupForm,
}) => {
  const {
    loadingBranchCountryApi,
    BranchCountryData = [], // array of countries

    loadingBranchStateApi,
    BranchStateData = [], // will be replaced per country

    loadingBranchCityApi,
    BranchCityData = [], // will be replaced per state
  } = useSelector((state) => state?.BranchReducer);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  // Strict validation - every contact MUST have name + 10-digit mobile
  const validateContacts = () => {
    if (!contact || contact.length === 0) {
      toast.error("Please add at least one contact");
      return false;
    }

    for (let i = 0; i < contact.length; i++) {
      const c = contact[i];

      // 1. Contact person name is mandatory
      if (!c.contact_person?.trim()) {
        toast.error(
          `Contact person name is required for contact form ${i + 1}`,
        );
        return false;
      }

      // 2. Mobile number is mandatory
      if (!c.mobile_number?.trim()) {
        toast.error(`Mobile number is required for contact form ${i + 1}`);
        return false;
      }

      // 3. Mobile number must be exactly 10 digits
      const mobile = c.mobile_number.trim();
      if (mobile.length !== 10) {
        toast.error(
          `Mobile number must be exactly 10 digits in contact form ${i + 1}`,
        );
        return false;
      }

      // 4. Optional: Check if mobile contains only digits
      if (!/^\d{10}$/.test(mobile)) {
        toast.error(
          `Mobile number must contain only digits in contact form ${i + 1}`,
        );
        return false;
      }
      const email = c.email?.trim();
      // Optional: Validate email format if provided
      if (!c.email?.trim()) {
        toast.error(`Email is required for contact form ${i + 1}`);
        return false;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error(`Invalid email format in contact form ${i + 1}`);
        return false;
      }

      // 5. Country is mandatory
      if (!c.country) {
        toast.error(`Country is required for contact form ${i + 1}`);
        return false;
      }
      // 6. State is mandatory
      if (!c.state) {
        toast.error(`State is required for contact form ${i + 1}`);
        return false;
      }
      // 7. City is mandatory
      if (!c.city) {
        toast.error(`City is required for contact form ${i + 1}`);
        return false;
      }
      // 8. Address is mandatory
      if (!c.address?.trim()) {
        toast.error(`Address is required for contact form ${i + 1}`);
        return false;
      }
      // 9. Pincode is mandatory
      if (!c.pincode?.trim()) {
        toast.error(`Pincode is required for contact form ${i + 1}`);
        return false;
      }
    }

    return true;
  };

  const submitLedgerForm = async () => {
    // Validate before API call
    if (!validateContacts()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${adminMainUrl}/api/account/ledger-account`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "auth-token": Cookies.get("token"),
            user_id: Cookies.get("flexichain_user_id"),
          },
          body: JSON.stringify({
            ledger_account_id,
            contact,
          }),
        },
      );
      const data = await response.json();

      if (response.ok) {
        toast.success(data?.msg || "Contact information saved successfully");
        localStorage.removeItem("ledger_account_id");
        saveAndNext(tabId);
        clearGroupForm();
      } else {
        toast.error(data?.msg || "Failed to save contact information");
        console.error("Error saving contacts:", data.message);
      }
    } catch (error) {
      toast.error("Network error while saving contacts");
      console.error("Network error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load countries once on mount
  useEffect(() => {
    if (!BranchCountryData?.length) {
      dispatch(getBranchCountry());
    }
  }, [dispatch, BranchCountryData?.length]);

  // Handle country change → load states
  const handleCountryChange = (index, e) => {
    const countryId = e.target.value; // assuming you're using numeric ID
    onContactChange(index, {
      target: { name: "country", value: countryId },
    });

    // Reset dependent fields
    onContactChange(index, { target: { name: "state", value: "" } });
    onContactChange(index, { target: { name: "city", value: "" } });

    // find country ID ONLY for API call
    const countryObj = BranchCountryData.find((c) => c.name == countryId);
    // Load states for selected country
    if (countryObj?.id) {
      dispatch(getBranchState(countryObj.id));
    }
  };

  // Handle state change → load cities
  const handleStateChange = (index, e) => {
    const stateId = e.target.value;
    onContactChange(index, {
      target: { name: "state", value: stateId },
    });

    // Reset city
    onContactChange(index, { target: { name: "city", value: "" } });

    // find state ID ONLY for API call
    const stateObj = BranchStateData.find((s) => s.name === stateId);

    if (stateObj?.id) {
      dispatch(getBranchCity(stateObj.id));
    }
  };

  const addNewContactSection = () => {
    if (!validateContacts()) {
      return;
    }
    addContact();
  };

  return (
    <>
      <style>
        {`
          .padding-pt-9 {
            padding: 10px;
          }
        `}
      </style>

      {contact.map((item, index) => (
        <div
          key={index}
          className="row bg-white p-3 mb-4 position-relative shadow-sm rounded"
        >
          <div className="d-flex justify-content-end  mb-3">
            {index > 0 && (
              <button
                type="button"
                onClick={() => removeContact(index)}
                className="btn btn-primary"
                title="Remove this contact"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
          {/* contact_person */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Contact Person <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="contact_person"
              className="form-control bg-light"
              placeholder="Enter contact person name"
              onChange={(e) => onContactChange(index, e)}
              value={item.contact_person || ""}
              required
            />
          </div>

          {/* mobile_number */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="mobile_number"
              className="form-control bg-light"
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              onChange={(e) => onContactChange(index, e)}
              value={item.mobile_number || ""}
              required
            />
          </div>

          {/* email */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              name="email"
              className="form-control bg-light"
              placeholder="Enter email address (optional)"
              onChange={(e) => onContactChange(index, e)}
              value={item.email || ""}
            />
          </div>

          {/* Country */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Country <span className="text-danger">*</span>
            </label>
            <select
              name="country"
              className={`form-select bg-light padding-pt-9 ${
                loadingBranchCountryApi ? "loading-select" : ""
              }`}
              value={item.country || ""}
              onChange={(e) => handleCountryChange(index, e)}
              disabled={loadingBranchCountryApi}
            >
              <option value="">Select Country</option>
              {BranchCountryData &&
                BranchCountryData.map((country) => (
                  <option key={country.id} value={country.name}>
                    {country.name} ({country.sort_name})
                  </option>
                ))}
            </select>
          </div>

          {/* State */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              State <span className="text-danger">*</span>
            </label>
            <select
              name="state"
              className={`form-select bg-light padding-pt-9 ${
                loadingBranchStateApi ? "loading-select" : ""
              }`}
              value={item.state || ""}
              onChange={(e) => handleStateChange(index, e)}
              disabled={!item.country || loadingBranchStateApi}
            >
              <option value="">Select State</option>
              {/* {BranchStateData &&
                BranchStateData.filter((s) => s.country_id == item.country).map(
                  (state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ),
                )} */}
              {BranchStateData &&
                BranchStateData?.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>

          {/* City */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              City <span className="text-danger">*</span>
            </label>
            <select
              name="city"
              className={`form-select bg-light padding-pt-9 ${
                loadingBranchCityApi ? "loading-select" : ""
              }`}
              value={item.city || ""}
              onChange={(e) => onContactChange(index, e)}
              disabled={!item.state || loadingBranchCityApi}
            >
              <option value="">Select City</option>
              {/* {BranchCityData &&
                BranchCityData?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))} */}
              {BranchCityData &&
                BranchCityData?.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>

          {/* pincode */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Pincode <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="pincode"
              className="form-control bg-light"
              placeholder="Enter pincode"
              maxLength={6}
              onChange={(e) => onContactChange(index, e)}
              value={item.pincode || ""}
            />
          </div>

          {/* address */}
          <div className="col-md-6 mb-4">
            <label className="form-label fw-medium">
              Address <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="address"
              className="form-control bg-light"
              placeholder="Enter full address"
              onChange={(e) => onContactChange(index, e)}
              value={item.address || ""}
            />
          </div>
        </div>
      ))}

      {/* Bottom Action Buttons */}
      {/* <div className="d-flex gap-2 mt-4 flex-wrap">
        <button
          onClick={() => saveAndNext(backId)}
          className="btn btn-primary border px-4"
          disabled={isSubmitting}
        >
          Back
        </button>

        <button
          onClick={submitLedgerForm}
          className="btn btn-primary px-4"
          disabled={isSubmitting}
        >
          <i className="fas fa-save me-1"></i>
          {isSubmitting ? "Saving..." : "Submit"}
        </button>

        <button
          className="btn btn-light border px-4"
          onClick={addContact}
          disabled={isSubmitting}
        >
          <i className="fas fa-plus me-1"></i> Add Contact
        </button>
      </div> */}

      <div className="d-flex flex-wrap gap-3 justify-content-between mt-2">
        <div className="d-flex gap-3">
          <button
            type="button"
            className="btn btn-primary px-5"
            onClick={() => saveAndNext(backId)}
          >
            Back
          </button>

          <button
            type="button"
            className="btn btn-primary px-5"
            onClick={submitLedgerForm}
          >
            Save & Next
          </button>
        </div>

        <button
          type="button"
          className="btn btn-light px-4"
          onClick={addNewContactSection}
        >
          <i className="fas fa-plus me-2"></i>Add Contact
        </button>
      </div>
    </>
  );
};

export default LedgerAccountContactInfo;
